import { CompaniesRepository } from '../repositories/companies.repository'
import {
    BusinessModelsGetListResponseT,
    CompaniesCreateCompanyResponseT,
    CompaniesDownloadExcelPayloadT,
    CompaniesDownloadExcelResponseT,
    CompaniesGenerateContractResponseT,
    CompaniesGetActivityResponse,
    CompaniesGetAgreementBasesResponseT,
    CompaniesGetAllListParamsT,
    CompaniesGetCompanyResponseT,
    CompaniesGetCompanySettingsParams,
    CompaniesGetCompanySettingsResponse,
    CompaniesGetCompanySourcesResponse,
    CompaniesGetCountriesAndManagersResponseT,
    CompaniesGetCountriesResponseT,
    CompaniesGetListBillingParams,
    CompaniesGetListParams,
    CompaniesGetListResponseT,
    CompaniesGetPeriodsResponseT,
    CompaniesGetSchedulesResponseT,
    CompaniesGetStagesResponseT,
    CompaniesGetSubdivisionsResponseT,
    CompaniesGetTariffsResponse,
    CompaniesGetTransportTypesResponseT,
    CompaniesGetWeightTypesResponseT,
    CompaniesUpdateCompanyResponseT,
    CompanyBillingOrderType,
    CompanyBillingTranscriptType,
    CompanyGenerateContractPayloadT,
    CompanyGetOrderTypesResponse,
    CompanyGetTranscriptTypesResponse,
    CompanyPayloadT,
    CompanySetBillingPayloadT,
    CompanySetClientPayloadT,
    GetCompaniesParams,
    GetCompanyContractsResponse,
    GetPayersResponseT,
} from '../types/companies.types'

const companiesRepository = new CompaniesRepository()

export class CompaniesService {
    async getAllList(params: CompaniesGetAllListParamsT): Promise<CompaniesGetListResponseT> {
        const response = await companiesRepository.getAllList(params)
        return response.data
    }

    async getCompanies(params: GetCompaniesParams): Promise<CompaniesGetListResponseT> {
        const response = await companiesRepository.getCompanies(params)
        return response.data
    }

    async getTransportTypes(): Promise<CompaniesGetTransportTypesResponseT> {
        const response = await companiesRepository.getTransportTypes()
        return response.data
    }

    async getCompany(company_id: number): Promise<CompaniesGetCompanyResponseT> {
        const response = await companiesRepository.getCompany(company_id)
        return response.data
    }

    async getCompanySettings(
        company_id: number,
        params: CompaniesGetCompanySettingsParams
    ): Promise<CompaniesGetCompanySettingsResponse> {
        const response = await companiesRepository.getCompanySettings(company_id, params)
        return response.data
    }

    async getList(params: CompaniesGetListParams): Promise<CompaniesGetListResponseT> {
        const response = await companiesRepository.getList(params)
        return response.data
    }

    async getListBilling(
        params: CompaniesGetListBillingParams
    ): Promise<CompaniesGetListResponseT> {
        const response = await companiesRepository.getListBilling(params)
        return response.data
    }

    async getSubdivisions(params: any): Promise<CompaniesGetSubdivisionsResponseT> {
        const response = await companiesRepository.getSubdivisions(params)
        return response.data
    }

    async getCountriesAndManagers(): Promise<CompaniesGetCountriesAndManagersResponseT> {
        const response = await companiesRepository.getCountriesAndManagers()
        return response.data
    }

    async getCountries(page: number, search?: string): Promise<CompaniesGetCountriesResponseT> {
        const response = await companiesRepository.getCountries(page, search)
        return response.data
    }

    async createCompany(
        company_payload: CompanyPayloadT
    ): Promise<CompaniesCreateCompanyResponseT> {
        const response = await companiesRepository.createCompany(company_payload)
        return response.data
    }

    async setClient(payload: CompanySetClientPayloadT): Promise<void> {
        const response = await companiesRepository.setClient(payload)
        return response.data
    }

    async getPayers(page: number, company_id: number): Promise<GetPayersResponseT> {
        const response = await companiesRepository.getPayers(page, company_id)
        return response.data
    }

    async sendPayers(company_id: number, payer_company_data: any[]): Promise<void> {
        const response = await companiesRepository.sendPayers(company_id, payer_company_data)
        return response.data
    }

    async resetPassword(email: string, password: string): Promise<void> {
        const response = await companiesRepository.resetPassword(email, password)
        return response.data
    }

    async changeManager(oldManager: number, newManager: number): Promise<void> {
        const response = await companiesRepository.changeManager(oldManager, newManager)
        return response.data
    }

    async changeManagerByExcel(form_data: FormData): Promise<void> {
        const response = await companiesRepository.changeManagerByExcel(form_data)
        return response.data
    }

    async updateCompany(
        company_id: number,
        company_payload: CompanyPayloadT
    ): Promise<CompaniesUpdateCompanyResponseT> {
        const response = await companiesRepository.updateCompany(company_id, company_payload)
        return response.data
    }

    async enableVerification(company_id: number, type_id: number): Promise<void> {
        await companiesRepository.enableVerification(company_id, type_id)
    }

    async setBilling(company_id: number, params: CompanySetBillingPayloadT): Promise<void> {
        await companiesRepository.setBilling(company_id, params)
    }

    async disableVerification(company_id: number): Promise<void> {
        await companiesRepository.disableVerification(company_id)
    }

    async activateCompany(company_id: number): Promise<void> {
        await companiesRepository.activateCompany(company_id)
    }

    async enableMassApprove(company_id: number): Promise<void> {
        await companiesRepository.enableMassApprove(company_id)
    }

    async disableMassApprove(company_id: number): Promise<void> {
        await companiesRepository.disableMassApprove(company_id)
    }

    async confirmCarrier(id: number): Promise<void> {
        await companiesRepository.confirmCarrier(id)
    }

    async deactivateCompany(company_id: number): Promise<void> {
        await companiesRepository.deactivateCompany(company_id)
    }

    async generateContract(
        company_id: number,
        contract_payload: CompanyGenerateContractPayloadT
    ): Promise<CompaniesGenerateContractResponseT> {
        const response = await companiesRepository.generateContract(company_id, contract_payload)
        return response.data
    }

    async getCompanyBases(): Promise<CompaniesGetAgreementBasesResponseT> {
        const response = await companiesRepository.getCompanyBases()
        return response.data
    }

    async getBusinessModels(): Promise<BusinessModelsGetListResponseT> {
        const response = await companiesRepository.getBusinessModels()
        return response.data
    }

    async activateCompanySetting(company_id: number, setting_id: number): Promise<void> {
        const response = await companiesRepository.activateCompanySetting(company_id, setting_id)
        return response.data
    }

    async disableCompanySetting(company_id: number, setting_id: number): Promise<void> {
        const response = await companiesRepository.disableCompanySetting(company_id, setting_id)
        return response.data
    }

    async resendCompanyContract(company_id: number): Promise<void> {
        const response = await companiesRepository.resendCompanyContract(company_id)
        return response.data
    }

    async resendCompanyTo1C(company_id: number): Promise<void> {
        const response = await companiesRepository.resendCompanyTo1C(company_id)
        return response.data
    }

    async sendToContract(company_id: number): Promise<void> {
        const response = await companiesRepository.sendToContract(company_id)
        return response.data
    }

    async getSchedules(params: any): Promise<CompaniesGetSchedulesResponseT> {
        const response = await companiesRepository.getSchedules(params)
        return response.data
    }

    async getActivity(
        companyId: number,
        createdAtFrom?: string | null,
        createdAtTo?: string | null
    ): Promise<CompaniesGetActivityResponse> {
        const response = await companiesRepository.getActivity(
            companyId,
            createdAtFrom,
            createdAtTo
        )
        return response.data
    }

    async getStages(page: number): Promise<CompaniesGetStagesResponseT> {
        const response = await companiesRepository.getStages(page)
        return response.data
    }

    async getPeriods(page: number): Promise<CompaniesGetPeriodsResponseT> {
        const response = await companiesRepository.getPeriods(page)
        return response.data
    }

    async getWeightTypes(page: number): Promise<CompaniesGetWeightTypesResponseT> {
        const response = await companiesRepository.getWeightTypes(page)
        return response.data
    }

    async getTariffs(): Promise<CompaniesGetTariffsResponse> {
        const response = await companiesRepository.getTariffs()
        return response.data
    }

    async getOrderTypes(): Promise<CompanyGetOrderTypesResponse> {
        const response = await companiesRepository.getOrderTypes()
        return response.data
    }

    async getTranscriptTypes(): Promise<CompanyGetTranscriptTypesResponse> {
        const response = await companiesRepository.getTranscriptTypes()
        return response.data
    }

    async getCompanySources(): Promise<CompaniesGetCompanySourcesResponse> {
        const response = await companiesRepository.getCompanySources()
        return response.data
    }

    async exportExcel(
        params: CompaniesDownloadExcelPayloadT
    ): Promise<CompaniesDownloadExcelResponseT> {
        const response = await companiesRepository.exportExcel(params)
        return response.data
    }

    async importCompanies(payload: FormData): Promise<void> {
        const response = await companiesRepository.importCompanies(payload)
        return response.data
    }

    async enableSetting(company_id: number, setting_id: number): Promise<void> {
        const response = await companiesRepository.enableSetting(company_id, setting_id)
        return response.data
    }

    async disableSetting(company_id: number, setting_id: number): Promise<void> {
        const response = await companiesRepository.disableSetting(company_id, setting_id)
        return response.data
    }

    async getCompanyContracts(companyId: number): Promise<GetCompanyContractsResponse> {
        const response = await companiesRepository.getCompanyContracts(companyId)
        return response.data
    }
}
